// TEMPORARY dev-only mock for the reports/documents domain.
//
// The reports domain was swapped from mock-first to real `apiClient` calls
// (src/api/reports/index.ts). Until the backend `reports` branch reaches the
// prod backend that every build points at, this lets you keep clicking through
// the Reports UI with fixture data on localhost/previews — it's served by the
// dev axios interceptor (src/api/devMock.ts) and ONLY when the "Auth bypass"
// dev toggle is on (isDevBypassAuth()). With bypass off, the real backend is
// used. **Delete this file + its wiring in devMock.ts once the reports backend
// is live on prod.**
import type { InternalAxiosRequestConfig } from 'axios';
import type {
  CustomerDocument,
  ReportFilters,
  ReportResult,
  ReportRow,
  ReportSummary,
  SavedReport,
  SavedReportKind,
  SavedReportPeriod,
  WasteTypeOption,
  YlvaLine,
} from './index';

type MockResult = { status: number; data: unknown };
export type ReportsMatcher = (
  config: InternalAxiosRequestConfig
) => MockResult | null;

// ── Fixtures (moved verbatim from the retired mock body) ─────────────────────
const WASTE_TYPES: WasteTypeOption[] = [
  { id: 'wt_mixed', name: 'Mixed waste' },
  { id: 'wt_bio', name: 'Biowaste' },
  { id: 'wt_card', name: 'Cardboard' },
  { id: 'wt_garden', name: 'Garden waste' },
];

const rand = () => Math.random().toString(36).slice(2, 8);

type BaseRow = Omit<ReportRow, 'currency' | 'amount'>;

const BASE_ROWS: BaseRow[] = [
  {
    id: 'r01',
    date: '2026-01-14',
    wasteType: 'Mixed waste',
    wasteTypeId: 'wt_mixed',
    equipment: '660 L container',
    event: 'emptying',
    weightKg: 82,
    weightSource: 'weighed',
    priceMinor: 2400,
    recyclingRatePct: 0,
    treatmentLocation: 'Tammervoima incineration',
  },
  {
    id: 'r02',
    date: '2026-01-28',
    wasteType: 'Biowaste',
    wasteTypeId: 'wt_bio',
    equipment: '240 L container',
    event: 'emptying',
    weightKg: 41,
    weightSource: 'weighed',
    priceMinor: 1500,
    recyclingRatePct: 95,
    treatmentLocation: 'Koukkujärvi biogas',
  },
  {
    id: 'r03',
    date: '2026-02-11',
    wasteType: 'Cardboard',
    wasteTypeId: 'wt_card',
    equipment: '660 L container',
    event: 'emptying',
    weightKg: 22,
    weightSource: 'default',
    priceMinor: 900,
    recyclingRatePct: 100,
    treatmentLocation: 'Paperinkeräys Oy',
  },
  {
    id: 'r04',
    date: '2026-02-25',
    wasteType: 'Mixed waste',
    wasteTypeId: 'wt_mixed',
    equipment: '660 L container',
    event: 'emptying',
    weightKg: 76,
    weightSource: 'weighed',
    priceMinor: 2400,
    recyclingRatePct: 0,
    treatmentLocation: 'Tammervoima incineration',
  },
  {
    id: 'r05',
    date: '2026-03-04',
    wasteType: 'Garden waste',
    wasteTypeId: 'wt_garden',
    equipment: '1 m³ sack',
    event: 'delivery',
    weightKg: 60,
    weightSource: 'default',
    priceMinor: 1800,
    recyclingRatePct: 90,
    treatmentLocation: 'Koukkujärvi composting',
  },
  {
    id: 'r06',
    date: '2026-03-18',
    wasteType: 'Biowaste',
    wasteTypeId: 'wt_bio',
    equipment: '240 L container',
    event: 'emptying',
    weightKg: 38,
    weightSource: 'weighed',
    priceMinor: 1500,
    recyclingRatePct: 95,
    treatmentLocation: 'Koukkujärvi biogas',
  },
  {
    id: 'r07',
    date: '2026-04-01',
    wasteType: 'Cardboard',
    wasteTypeId: 'wt_card',
    equipment: '660 L container',
    event: 'emptying',
    weightKg: 25,
    weightSource: 'weighed',
    priceMinor: 900,
    recyclingRatePct: 100,
    treatmentLocation: 'Paperinkeräys Oy',
  },
  {
    id: 'r08',
    date: '2026-04-15',
    wasteType: 'Mixed waste',
    wasteTypeId: 'wt_mixed',
    equipment: '660 L container',
    event: 'emptying',
    weightKg: 88,
    weightSource: 'weighed',
    priceMinor: 2400,
    recyclingRatePct: 0,
    treatmentLocation: 'Tammervoima incineration',
  },
  {
    id: 'r09',
    date: '2026-04-29',
    wasteType: 'Garden waste',
    wasteTypeId: 'wt_garden',
    equipment: '1 m³ sack',
    event: 'delivery',
    weightKg: 55,
    weightSource: 'default',
    priceMinor: 1800,
    recyclingRatePct: 90,
    treatmentLocation: 'Koukkujärvi composting',
  },
  {
    id: 'r10',
    date: '2026-05-13',
    wasteType: 'Biowaste',
    wasteTypeId: 'wt_bio',
    equipment: '240 L container',
    event: 'emptying',
    weightKg: 44,
    weightSource: 'weighed',
    priceMinor: 1500,
    recyclingRatePct: 95,
    treatmentLocation: 'Koukkujärvi biogas',
  },
  {
    id: 'r11',
    date: '2026-05-27',
    wasteType: 'Mixed waste',
    wasteTypeId: 'wt_mixed',
    equipment: '660 L container',
    event: 'emptying',
    weightKg: 79,
    weightSource: 'weighed',
    priceMinor: 2400,
    recyclingRatePct: 0,
    treatmentLocation: 'Tammervoima incineration',
  },
  {
    id: 'r12',
    date: '2026-06-10',
    wasteType: 'Cardboard',
    wasteTypeId: 'wt_card',
    equipment: '660 L container',
    event: 'emptying',
    weightKg: 20,
    weightSource: 'default',
    priceMinor: 900,
    recyclingRatePct: 100,
    treatmentLocation: 'Paperinkeräys Oy',
  },
];

const BUSINESS_EXTRAS: Record<
  string,
  { fillDegreePct: number; energyUsePct: number }
> = {
  r01: { fillDegreePct: 85, energyUsePct: 100 },
  r02: { fillDegreePct: 70, energyUsePct: 0 },
  r03: { fillDegreePct: 60, energyUsePct: 0 },
  r04: { fillDegreePct: 80, energyUsePct: 100 },
  r05: { fillDegreePct: 90, energyUsePct: 0 },
  r06: { fillDegreePct: 65, energyUsePct: 0 },
  r07: { fillDegreePct: 55, energyUsePct: 0 },
  r08: { fillDegreePct: 92, energyUsePct: 100 },
  r09: { fillDegreePct: 88, energyUsePct: 0 },
  r10: { fillDegreePct: 72, energyUsePct: 0 },
  r11: { fillDegreePct: 83, energyUsePct: 100 },
  r12: { fillDegreePct: 58, energyUsePct: 0 },
};

const YLVA_LINES: YlvaLine[] = [
  {
    id: 'y1',
    wasteOrigin: 'Household (housing company)',
    generatingActivity: 'Residential',
    lowCode: '20 03 01',
    fraction: 'Mixed municipal waste',
    amountKg: 4180,
    placementCode: 'R1',
    treatmentLocation: 'Tammervoima',
    treatmentMethod: 'Energy recovery',
    selfDelivered: false,
  },
  {
    id: 'y2',
    wasteOrigin: 'Household (housing company)',
    generatingActivity: 'Residential',
    lowCode: '20 01 08',
    fraction: 'Biodegradable kitchen waste',
    amountKg: 2050,
    placementCode: 'R3',
    treatmentLocation: 'Koukkujärvi',
    treatmentMethod: 'Anaerobic digestion',
    selfDelivered: false,
  },
  {
    id: 'y3',
    wasteOrigin: 'Household (housing company)',
    generatingActivity: 'Residential',
    lowCode: '20 01 01',
    fraction: 'Paper and cardboard',
    amountKg: 920,
    placementCode: 'R3',
    treatmentLocation: 'Paperinkeräys Oy',
    treatmentMethod: 'Recycling',
    selfDelivered: false,
  },
  {
    id: 'y4',
    wasteOrigin: 'Household (housing company)',
    generatingActivity: 'Garden',
    lowCode: '20 02 01',
    fraction: 'Biodegradable garden waste',
    amountKg: 1150,
    placementCode: 'R3',
    treatmentLocation: 'Koukkujärvi',
    treatmentMethod: 'Composting',
    selfDelivered: true,
  },
];

let SAVED_REPORTS: SavedReport[] = [
  {
    id: 'sr_seed',
    name: 'Q1 2026 activity',
    kind: 'private',
    createdAt: '2026-04-02T09:00:00Z',
    period: { from: '2026-01-01', to: '2026-03-31' },
    propertyId: 'seed',
  },
];

let DOCUMENTS: CustomerDocument[] = [
  {
    id: 'doc_seed',
    filename: 'waste-agreement-2026.pdf',
    sizeBytes: 184_320,
    contentType: 'application/pdf',
    uploadedAt: '2026-05-20T12:00:00Z',
    status: 'ready',
  },
];

const UPLOADED_BLOBS = new Map<string, File>();

// ── Helpers (moved verbatim from the retired mock body) ──────────────────────
const inRange = (row: BaseRow, input: ReportFilters) => {
  if (row.date < input.from || row.date > input.to) return false;
  if (
    input.wasteTypeIds &&
    input.wasteTypeIds.length > 0 &&
    !input.wasteTypeIds.includes(row.wasteTypeId)
  ) {
    return false;
  }
  if (
    input.eventType &&
    input.eventType !== 'all' &&
    row.event !== input.eventType
  ) {
    return false;
  }
  return true;
};

const toReportRow = (base: BaseRow, business: boolean): ReportRow => ({
  ...base,
  amount: 1,
  currency: 'EUR',
  ...(business ? BUSINESS_EXTRAS[base.id] : {}),
});

const summarize = (rows: ReportRow[]): ReportSummary => {
  const totalWeightKg = rows.reduce((s, r) => s + r.weightKg, 0);
  const totalPriceMinor = rows.reduce((s, r) => s + r.priceMinor, 0);
  const avgRecyclingRatePct = rows.length
    ? Math.round(rows.reduce((s, r) => s + r.recyclingRatePct, 0) / rows.length)
    : 0;
  return {
    totalWeightKg,
    totalPriceMinor,
    currency: 'EUR',
    avgRecyclingRatePct,
    eventCount: rows.length,
  };
};

const buildResult = (input: ReportFilters, business: boolean): ReportResult => {
  const rows = BASE_ROWS.filter((r) => inRange(r, input)).map((r) =>
    toReportRow(r, business)
  );
  return { rows, summary: summarize(rows), wasteTypes: WASTE_TYPES };
};

// Report filters arrive as axios query params (`waste_type`, `event`).
const filtersFromParams = (
  config: InternalAxiosRequestConfig
): ReportFilters => {
  const p = (config.params ?? {}) as {
    from?: string;
    to?: string;
    waste_type?: string[];
    event?: ReportFilters['eventType'];
  };
  return {
    propertyId: '',
    from: p.from ?? '',
    to: p.to ?? '',
    wasteTypeIds: p.waste_type,
    eventType: p.event,
  };
};

const XLSX_MIME =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const exportBlob = (format: unknown, label: string): Blob =>
  new Blob([`mock ${label} ${String(format)} export`], {
    type: format === 'csv' ? 'text/csv' : XLSX_MIME,
  });

const url = (config: InternalAxiosRequestConfig) =>
  (config.url || '').replace(/^\/+/, '');
const method = (config: InternalAxiosRequestConfig) =>
  config.method?.toUpperCase();
const params = (config: InternalAxiosRequestConfig): Record<string, unknown> =>
  (config.params ?? {}) as Record<string, unknown>;

// ── Matchers (specific routes first) ─────────────────────────────────────────
export const reportsDevMatchers: ReportsMatcher[] = [
  // Report exports (blob) — before the plain report matcher.
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^property\/[^/?]+\/report\/export(?:\?|$)/.test(url(config)))
      return null;
    return { status: 200, data: exportBlob(params(config).format, 'xlsx') };
  },
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^property\/[^/?]+\/report\/ylva\/export(?:\?|$)/.test(url(config)))
      return null;
    return { status: 200, data: exportBlob(params(config).format, 'ylva') };
  },
  // YLVA report.
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^property\/[^/?]+\/report\/ylva(?:\?|$)/.test(url(config)))
      return null;
    return {
      status: 200,
      data: {
        year: Number(params(config).year) || new Date().getFullYear(),
        propertyId: '',
        businessType: 'housing_company',
        lines: YLVA_LINES,
        generatedAt: '2026-06-30T23:59:00Z',
      },
    };
  },
  // Business report.
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^property\/[^/?]+\/report\/business(?:\?|$)/.test(url(config)))
      return null;
    return { status: 200, data: buildResult(filtersFromParams(config), true) };
  },
  // Private report.
  (config) => {
    if (method(config) !== 'GET') return null;
    if (!/^property\/[^/?]+\/report(?:\?|$)/.test(url(config))) return null;
    return { status: 200, data: buildResult(filtersFromParams(config), false) };
  },
  // Saved-report export (blob) — before the delete/list matchers.
  (config) => {
    if (method(config) !== 'GET') return null;
    if (
      !/^property\/[^/?]+\/saved-reports\/[^/?]+\/export(?:\?|$)/.test(
        url(config)
      )
    )
      return null;
    return {
      status: 200,
      data: exportBlob(params(config).format, 'saved-report'),
    };
  },
  // Saved reports: list (GET) / create (POST).
  (config) => {
    if (!/^property\/[^/?]+\/saved-reports(?:\?|$)/.test(url(config)))
      return null;
    if (method(config) === 'GET')
      return { status: 200, data: { reports: SAVED_REPORTS } };
    if (method(config) === 'POST') {
      const body = (config.data ?? {}) as {
        name?: string;
        kind?: SavedReportKind;
        period?: SavedReportPeriod;
      };
      const report: SavedReport = {
        id: `sr_${rand()}`,
        name: (body.name ?? '').trim() || 'Untitled report',
        kind: body.kind ?? 'private',
        createdAt: new Date().toISOString(),
        period: body.period ?? { from: '', to: '' },
        propertyId: '',
      };
      SAVED_REPORTS = [report, ...SAVED_REPORTS];
      return { status: 201, data: report };
    }
    return null;
  },
  // Saved-report delete.
  (config) => {
    if (method(config) !== 'DELETE') return null;
    const m = /^property\/[^/?]+\/saved-reports\/([^/?]+)(?:\?|$)/.exec(
      url(config)
    );
    if (!m) return null;
    SAVED_REPORTS = SAVED_REPORTS.filter((r) => r.id !== m[1]);
    return { status: 204, data: undefined };
  },
  // Document download (blob) — before the delete matcher.
  (config) => {
    if (method(config) !== 'GET') return null;
    const m = /^property\/[^/?]+\/documents\/([^/?]+)\/download(?:\?|$)/.exec(
      url(config)
    );
    if (!m) return null;
    const doc = DOCUMENTS.find((d) => d.id === m[1]);
    const uploaded = doc && UPLOADED_BLOBS.get(doc.id);
    if (uploaded) return { status: 200, data: uploaded };
    return {
      status: 200,
      data: new Blob([`mock document ${doc?.filename ?? m[1]}`], {
        type: doc?.contentType || 'application/octet-stream',
      }),
    };
  },
  // Documents: list (GET) / upload (POST).
  (config) => {
    if (!/^property\/[^/?]+\/documents(?:\?|$)/.test(url(config))) return null;
    if (method(config) === 'GET')
      return { status: 200, data: { documents: DOCUMENTS } };
    if (method(config) === 'POST') {
      const file =
        config.data instanceof FormData
          ? (config.data.get('file') as File | null)
          : null;
      const doc: CustomerDocument = {
        id: `doc_${rand()}`,
        filename: file?.name ?? 'upload',
        sizeBytes: file?.size ?? 0,
        contentType: file?.type || 'application/octet-stream',
        uploadedAt: new Date().toISOString(),
        status: 'scanning',
      };
      DOCUMENTS = [doc, ...DOCUMENTS];
      if (file) UPLOADED_BLOBS.set(doc.id, file);
      // Demo the virus-scan UX: flip scanning → ready after a beat so the
      // documents query's auto-poll settles the badge.
      const timer = setTimeout(() => {
        const stored = DOCUMENTS.find((d) => d.id === doc.id);
        if (stored) stored.status = 'ready';
      }, 2500);
      (timer as { unref?: () => void }).unref?.();
      return { status: 201, data: doc };
    }
    return null;
  },
  // Document delete.
  (config) => {
    if (method(config) !== 'DELETE') return null;
    const m = /^property\/[^/?]+\/documents\/([^/?]+)(?:\?|$)/.exec(
      url(config)
    );
    if (!m) return null;
    DOCUMENTS = DOCUMENTS.filter((d) => d.id !== m[1]);
    UPLOADED_BLOBS.delete(m[1]);
    return { status: 204, data: undefined };
  },
];
